import {
  useEffect, useState, type ReactNode,
} from 'react'
import {
  ChevronDown,
  CreditCard,
  LayoutDashboard,
  LayoutGrid,
  Mail,
  MessageSquare,
  Search,
  ShieldCheck,
  Smartphone,
  UserRound,
} from 'lucide-react'
import {
  isSettingsPage,
  matchSettingsPages,
  pageForSection,
  pageFromSearch,
  SETTINGS_PAGES,
  type SettingsPageId,
} from '../utils/settingsNav'
import {
  isSettingsSection,
} from '../utils/settingsSections'
import {
  reveal,
} from '../hooks/useSettingsSectionDeepLink'

const PAGE_ICONS: Record<SettingsPageId, ReactNode> = {
  overview: <LayoutDashboard className="w-4 h-4" />,
  mail: <Mail className="w-4 h-4" />,
  chat: <MessageSquare className="w-4 h-4" />,
  apps: <LayoutGrid className="w-4 h-4" />,
  assistant: <UserRound className="w-4 h-4" />,
  permissions: <ShieldCheck className="w-4 h-4" />,
  phone: <Smartphone className="w-4 h-4" />,
  billing: <CreditCard className="w-4 h-4" />,
}

const GROUPS = [...new Set(SETTINGS_PAGES.map((page) => page.group))]

/** Attribute naming the page a panel holds, so a `#anchor` finds its page. */
const PAGE_ATTRIBUTE = 'data-settings-page'

/**
 * The page a `#hash` link opens: a page by id, a section by its mapping (which
 * holds even before the section has rendered), or else whichever page
 * contains an element with that id, such as the location steps inside the
 * Phone page.
 */
function pageForHash(target: string): SettingsPageId | null {
  if (isSettingsPage(target)) return target
  if (isSettingsSection(target)) return pageForSection(target)
  const owner = document.getElementById(target)?.closest(`[${PAGE_ATTRIBUTE}]`)
  const page = owner?.getAttribute(PAGE_ATTRIBUTE) ?? ''
  return isSettingsPage(page) ? page : null
}

/** Where a page link scrolls to: the top of the settings layout. */
const SETTINGS_TOP_ID = 'settings-top'

/**
 * Settings as a sidebar of named pages (`settings-sidebar`), opening on the
 * Overview; on a phone the sidebar folds into a menu under the current page's
 * name.
 *
 * Every page stays mounted and the others are `hidden`: the cards load their
 * own data on mount, so moving between pages never refetches or drops a
 * half-filled form, and `useSettingsSectionDeepLink` can still find a section
 * by id. A modal a page owns is inside that hidden subtree too, which is why
 * a deep link that opens one also names the page (`requestedPage`).
 *
 * Links: `?section=<id>` opens the page holding that section; a `#<section>`
 * link (the announcement banner's CTAs) switches to its page and scrolls to
 * it, since a hidden target gives the browser nothing to jump to; a
 * `#<page>` link (the Overview's rows) opens that page; and any other
 * `#<id>` opens the page containing that element.
 */
export function SettingsNav({
  pages,
  requestedPage,
}: {
  pages: Record<SettingsPageId, ReactNode>
  requestedPage?: SettingsPageId | null
}) {
  const [active, setActive] = useState<SettingsPageId>(
    () => pageFromSearch(window.location.search) ?? SETTINGS_PAGES[0].id,
  )
  // Phones only: whether the folded menu is open.
  const [menuOpen, setMenuOpen] = useState(false)
  const [query, setQuery] = useState('')
  const matches = matchSettingsPages(query)

  const open = (page: SettingsPageId) => {
    setActive(page)
    setMenuOpen(false)
    setQuery('')
  }

  useEffect(() => {
    if (requestedPage) open(requestedPage)
  }, [requestedPage])

  useEffect(() => {
    const openHashSection = () => {
      const target = window.location.hash.slice(1)
      const page = pageForHash(target)
      if (!page) return
      open(page)
      // Clear it so the same link works twice: an unchanged hash fires no event.
      // A card that reads the hash itself (ShortcutSection's location steps)
      // has already done so: its listener, a child's effect, was added first.
      history.replaceState(null, '', window.location.pathname + window.location.search)
      // An anchor is scrolled to and highlighted like a `?section=` link; a
      // page link only brings the top of the layout into view.
      requestAnimationFrame(() => {
        if (isSettingsPage(target)) {
          document.getElementById(SETTINGS_TOP_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          return
        }
        const anchor = document.getElementById(target)
        if (anchor) reveal(anchor)
      })
    }
    openHashSection()
    window.addEventListener('hashchange', openHashSection)
    return () => window.removeEventListener('hashchange', openHashSection)
  }, [])

  const activePage = SETTINGS_PAGES.find((page) => page.id === active) ?? SETTINGS_PAGES[0]

  return (
    <div id={SETTINGS_TOP_ID} className="scroll-mt-24 md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:gap-8">
      <button
        type="button"
        onClick={() => setMenuOpen((current) => !current)}
        aria-expanded={menuOpen}
        aria-controls="settings-menu"
        className="mb-4 flex w-full items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900 md:hidden"
      >
        <span className="text-gray-500">{PAGE_ICONS[activePage.id]}</span>
        <span className="flex-1">{activePage.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
      </button>
      <nav
        id="settings-menu"
        aria-label="Settings"
        className={`${menuOpen ? 'mb-6 block rounded-xl border border-gray-200 bg-white p-2' : 'hidden'} md:mb-0 md:block md:rounded-none md:border-0 md:bg-transparent md:p-0`}
      >
        <label className="relative mb-4 block">
          <Search className="pointer-events-none absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              const first = matches[0]
              if (e.key === 'Enter' && first) open(first.id)
            }}
            placeholder="Search settings"
            aria-label="Search settings"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </label>
        {matches.length === 0 && (
          <p className="px-3 text-sm text-gray-500">No setting matches that. Try another word.</p>
        )}
        {GROUPS.map((group) => {
          const items = matches.filter((page) => page.group === group)
          if (items.length === 0) return null
          return (
            <div key={group} className="mb-4 last:mb-0 md:last:mb-4">
              {group !== 'Overview' && (
                <p className="mb-1 px-3 text-xs font-medium text-gray-400">{group}</p>
              )}
              <ul>
                {items.map((page) => {
                  const selected = page.id === active
                  return (
                    <li key={page.id}>
                      <button
                        type="button"
                        onClick={() => open(page.id)}
                        aria-current={selected ? 'page' : undefined}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 md:py-2 text-left text-sm transition-colors ${
                          selected
                            ? 'bg-gray-100 font-medium text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span className="text-gray-500">{PAGE_ICONS[page.id]}</span>
                        <span className="flex-1">{page.label}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </nav>

      <div>
        <div className="mb-5">
          <h2 className="text-xl font-semibold text-gray-900">{activePage.label}</h2>
          <p className="mt-0.5 text-sm text-gray-500">{activePage.description}</p>
        </div>
        {SETTINGS_PAGES.map((page) => (
          <section
            key={page.id}
            aria-label={page.label}
            hidden={page.id !== active}
            {...{ [PAGE_ATTRIBUTE]: page.id }}
          >
            {pages[page.id]}
          </section>
        ))}
      </div>
    </div>
  )
}
